import React from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import css from './Analysis.css';
import ToggleTabGroup from '../../../../components/atoms/ToggleTabGroup';
import EmptyTable from '../../../../resources/icons/empty-analysis.svg';
import { EVENT, UNIQUE_EVENT } from '../consts';

const COLORS = ['#4BB3FD', '#5D5FEF', '#5ED3B0'];
const translatePrefix = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.REPORTING.ANALYSIS';
const metadataTranslatePrefix = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.REPORTING.METADATA';

const Analysis = ({ onEventToggle, selected, chartData, testChannels, isEmpty = false, translate }) => {
    const seriesData = isEmpty
        ? []
        : testChannels.map((testChannel, index) => ({
              name: testChannel.adnetworkDisplayName,
              data: chartData[testChannel.maxRange]?.[selected] || [],
              realData: chartData[testChannel.maxRange]?.[`${selected}_value`] || [],
              color: COLORS[index],
              lineWidth: 2,
          }));

    const chartOptions = {
        chart: {
            type: 'line',
            backgroundColor: 'transparent',
            height: null,
            width: null,
        },
        title: {
            text: null,
        },
        xAxis: {
            categories: chartData.dates || [],
        },
        yAxis: {
            visible: false,
        },
        legend: {
            enabled: false,
        },
        tooltip: {
            shared: true,
            formatter() {
                let tooltip = `<b>${this.x}</b><br/>`;
                this.points.forEach(function(point) {
                    const realValue = point.series.options.realData?.[point.point.index] || 0;
                    tooltip += `<span style="color:${point.color}">\u25CF</span> ${
                        point.series.name
                    }: ${realValue.toLocaleString()}<br/>`;
                });
                return tooltip;
            },
        },
        series: seriesData,
        credits: {
            enabled: false,
        },
        exporting: {
            enabled: false,
        },
        responsive: {
            rules: [
                {
                    condition: {
                        maxWidth: 500,
                    },
                },
            ],
        },
    };

    const toggleTabGroupOptions = [
        {
            option: EVENT,
            label: translate(`${metadataTranslatePrefix}.EVENT`),
        },
        {
            option: UNIQUE_EVENT,
            label: translate(`${metadataTranslatePrefix}.EVENT_UNIQUE`),
        },
    ];

    return (
        <div className={css.incrementalityContainer}>
            <div className={css.incrementalityHeader}>
                <div className={css.incrementalityTitleSection}>
                    <h2 className={css.panelTitle}>
                        <Translate id={`${translatePrefix}.TITLE`} />
                    </h2>
                    <p className={css.panelSubtitle}>
                        <Translate id={`${translatePrefix}.SUBTITLE`} />
                    </p>
                </div>
                <div className={css.incrementalityTabs}>
                    <ToggleTabGroup
                        options={toggleTabGroupOptions}
                        selectedOption={selected}
                        onToggle={onEventToggle}
                    />
                </div>
            </div>

            {!isEmpty && (
                <div className={css.chartTitleRow}>
                    <h3 className={css.chartTitle}>
                        <Translate id={`${translatePrefix}.Y_LABEL`} />
                    </h3>
                    <div className={css.chartLegend}>
                        {testChannels.map((testChannel, index) => (
                            <div key={testChannel.id} className={css.legendItem}>
                                <div className={css.legendColor} style={{ backgroundColor: COLORS[index] }} />
                                <span className={css.legendText}>{testChannel.adnetworkDisplayName}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className={css.chartArea}>
                {isEmpty ? (
                    <div className={css.emptyContainer}>
                        <div className={css.emptyData}>
                            <Translate id={`${translatePrefix}.NO_DATA`} />
                        </div>
                        <EmptyTable />
                    </div>
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                )}
            </div>
        </div>
    );
};

Analysis.propTypes = {
    selected: PropTypes.string,
    onEventToggle: PropTypes.func,
    isEmpty: PropTypes.bool,
    chartData: PropTypes.any,
    testChannels: PropTypes.arrayOf(
        PropTypes.shape({
            adnetworkName: PropTypes.string,
            adnetworkDisplayName: PropTypes.string,
        })
    ),
    translate: PropTypes.func,
};

export default withLocalize(Analysis);
